/**
 * El diagnóstico de quien llega a la landing SIN la ficha conectada: el
 * buscador del diseño V4 (`V4_Buscador`), en tres pasos.
 *
 *   1. Busca su negocio (autocompletado de Google, solo Portugal). Si su
 *      Tapstar ya apunta a una ficha, se le propone esa y no tiene que buscar.
 *   2. Confirma la palabra clave: la categoría principal de su ficha y las
 *      secundarias, o una suya.
 *   3. «Análise em direto»: el escaneo del mapa corre en el servidor y aquí se
 *      sondea el progreso. Al terminar, la landing se vuelve a leer y las cuatro
 *      tarjetas del ponto de partida salen con sus números.
 *
 * Nada de esto cuesta hasta el paso 2 (preparar) y el 3 (lanzar), que son
 * clics; buscar sí consulta a Google, con debounce. Los topes (intentos por
 * persona y por día) los pone el servidor — aquí solo se traducen sus códigos.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, MapPin, Search } from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import * as API_REAL from '@/api/ofertaPtService';

import { DIAGNOSTICO as C, PONTO_DE_PARTIDA } from './copy';

const MIN_QUERY = 3;
const DEBOUNCE_MS = 350;

const CARTAO = 'flex flex-col gap-3 rounded-[22px] bg-white p-[18px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-[#ececf0] md:p-6';
const PASSO = 'text-xs font-bold uppercase tracking-[0.6px] text-[#9a5700]';
const BOTAO = 'inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-[14px] bg-[#feae2c] px-5 text-base font-bold text-[#1d1d1f] hover:bg-[#f59e0b] disabled:cursor-wait disabled:opacity-70 md:w-auto';

function codigoDe(e) {
  return e?.response?.data?.code || 'provedor';
}

/** Paso 3: la análise en directo, con el progreso que devuelve el servidor. */
function EmDireto({ palavra, progresso }) {
  const total = progresso?.total || 49;
  const feitos = Math.min(progresso?.feitos || 0, total);
  const pct = Math.max(8, Math.round((feitos / total) * 100));
  const itens = C.checklist(palavra);
  // El primero se da por hecho al lanzar; el segundo en cuanto hay progreso; el
  // tercero es el que gira hasta que llegan los números.
  const hechos = [true, feitos > 0, false];
  return (
    <div className={CARTAO} aria-live="polite">
      <span className={PASSO}>{C.passo3}</span>
      <span className="text-lg font-extrabold">{C.aAnalisar}</span>
      <div className="h-2 overflow-hidden rounded-full bg-[#ececf0]">
        <div className="h-2 rounded-full bg-[#feae2c] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {itens.map((t, i) => (
          <li key={t} className={`flex items-center gap-2.5 text-sm ${!hechos[i] && (i === 0 || hechos[i - 1]) ? 'font-semibold' : ''}`}>
            {hechos[i] ? (
              <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#e8f6ec]">
                <Check className="h-3 w-3 text-[#1d6b34]" aria-hidden="true" />
              </span>
            ) : (
              <Loader2 className="h-[22px] w-[22px] shrink-0 animate-spin text-[#feae2c]" aria-hidden="true" />
            )}
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * @param {{ token?: string, prefill: {placeId: string, nome: string}|null,
 *           restantes: number, diagnostico: object|null, landingKey: unknown[],
 *           api?: typeof API_REAL }} props
 *   `api`: la página de capturas pasa una simulada para revisar los tres pasos
 *   sin backend; la landing usa la real.
 */
export default function DiagnosticoFlow({
  token, prefill, restantes, diagnostico, landingKey, api = API_REAL,
}) {
  const {
    buscarNegocios, getDiagnostico, lancarDiagnostico, prepararDiagnostico,
  } = api;
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [resultados, setResultados] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [objetivo, setObjetivo] = useState(null);
  const [palavra, setPalavra] = useState('');
  const [outra, setOutra] = useState('');
  const [aCarregar, setACarregar] = useState(false);
  const [erro, setErro] = useState(diagnostico?.estado === 'erro' ? 'erro' : null);
  const [usarPrefill, setUsarPrefill] = useState(!!prefill);
  // El negocio pulsado mientras el servidor lo prepara (DataForSEO, unos
  // segundos): la lista se cierra al momento y queda él con su carga. Sin esto
  // la lista seguía abierta y parecía que el click no había hecho nada.
  const [escolhido, setEscolhido] = useState(null);
  const ultima = useRef('');

  const analisando = diagnostico?.estado === 'a_analisar';

  // Mientras se analiza, se pregunta el estado; al cambiar, se relee la landing
  // para que las tarjetas salgan con los números.
  const { data: estado } = useQuery({
    queryKey: [...landingKey, 'diagnostico'],
    queryFn: () => getDiagnostico(token),
    enabled: analisando,
    refetchInterval: 2500,
  });
  useEffect(() => {
    const e = estado?.diagnostico?.estado;
    if (e && e !== 'a_analisar') {
      if (e === 'erro') setErro('erro');
      queryClient.invalidateQueries({ queryKey: landingKey });
    }
  }, [estado, landingKey, queryClient]);

  // Autocompletado con debounce. `ultima` descarta respuestas que llegan
  // después de una búsqueda más nueva.
  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      setResultados(null);
      return undefined;
    }
    const id = setTimeout(async () => {
      ultima.current = q;
      setBuscando(true);
      try {
        const r = await buscarNegocios(token, q);
        if (ultima.current === q) setResultados(r);
      } catch (e) {
        if (ultima.current === q) setErro(codigoDe(e));
      } finally {
        if (ultima.current === q) setBuscando(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query, token, buscarNegocios]);

  if (analisando) {
    return <EmDireto palavra={diagnostico.palavraChave} progresso={estado?.progresso} />;
  }

  if (restantes <= 0) {
    return <p className="m-0 rounded-2xl bg-[#fff4dd] p-4 text-sm font-semibold text-[#6b3d00]">{C.semTentativas}</p>;
  }

  const escolher = async (placeId, negocio = null) => {
    setErro(null);
    setEscolhido(negocio);
    setACarregar(true);
    try {
      const obj = await prepararDiagnostico(token, placeId);
      setObjetivo(obj);
      setPalavra(obj.palavras[0] || '');
      setOutra('');
    } catch (e) {
      setErro(codigoDe(e));
    } finally {
      setEscolhido(null);
      setACarregar(false);
    }
  };

  const lancar = async () => {
    const final = (palavra === '__outra__' ? outra : palavra).trim();
    if (!final) {
      setErro('sem_categoria');
      return;
    }
    setErro(null);
    setACarregar(true);
    try {
      await lancarDiagnostico(token, { placeId: objetivo.placeId, palavra: final });
      await queryClient.invalidateQueries({ queryKey: landingKey });
    } catch (e) {
      setErro(codigoDe(e));
    } finally {
      setACarregar(false);
    }
  };

  const mensagemErro = erro && (
    <p role="alert" className="m-0 rounded-xl bg-[#fdecea] px-3.5 py-2.5 text-sm font-semibold text-[#8a1c12]">
      {C.erros[erro] || C.erros.provedor}
    </p>
  );

  // Paso 2: la palabra clave.
  if (objetivo) {
    return (
      <div className={CARTAO}>
        <span className={PASSO}>{C.passo2}</span>
        <span className="text-sm text-[#5e5e66]">
          <MapPin className="mr-1 inline h-4 w-4 align-[-3px]" aria-hidden="true" />
          {objetivo.nome}{objetivo.morada ? ` · ${objetivo.morada}` : ''}
        </span>
        <span className="text-lg font-extrabold">{C.procuramTe}</span>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={C.procuramTe}>
          {[...objetivo.palavras, '__outra__'].map((p) => {
            const activa = palavra === p;
            return (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={activa}
                onClick={() => setPalavra(p)}
                className={`min-h-[44px] rounded-full border px-3.5 text-sm font-semibold ${
                  activa ? 'border-[#1d1d1f] bg-[#1d1d1f] text-white'
                    : p === '__outra__' ? 'border-dashed border-[#c7c7cc] bg-white text-[#4a4a52]'
                      : 'border-[#e5e5ea] bg-white text-[#1d1d1f]'}`}
              >
                {p === '__outra__' ? C.outraPalavra : p}
              </button>
            );
          })}
        </div>
        {palavra === '__outra__' && (
          <label className="flex flex-col gap-1.5 text-sm font-semibold">
            <span className="sr-only">{C.outraPalavra}</span>
            <input
              type="text"
              value={outra}
              maxLength={60}
              autoFocus
              onChange={(e) => setOutra(e.target.value)}
              placeholder={C.outraPlaceholder}
              className="min-h-[48px] rounded-xl border-2 border-[#feae2c] px-3.5 text-base font-normal outline-none"
            />
          </label>
        )}
        <span className="text-[13px] leading-snug text-[#5e5e66]">{C.notaPalavra}</span>
        {mensagemErro}
        <CtaLink as="button" type="button" cta="landing.oferta-pt.analisar" onClick={lancar} disabled={aCarregar} className={BOTAO}>
          {aCarregar && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {C.verResultado}
        </CtaLink>
      </div>
    );
  }

  // Paso 1 con la ficha de su Tapstar: un clic y a la palabra clave.
  if (usarPrefill && prefill) {
    return (
      <div className={CARTAO}>
        <span className={PASSO}>{C.eEste}</span>
        <span className="flex items-center gap-2.5 text-base font-bold">
          <MapPin className="h-5 w-5 shrink-0 text-[#9a5700]" aria-hidden="true" />
          {prefill.nome || '—'}
        </span>
        {mensagemErro}
        <div className="flex flex-col gap-2 md:flex-row">
          <button type="button" onClick={() => escolher(prefill.placeId)} disabled={aCarregar} className={BOTAO}>
            {aCarregar && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {C.sim}
          </button>
          <button
            type="button"
            onClick={() => setUsarPrefill(false)}
            className="min-h-[52px] rounded-[14px] border border-[#e5e5ea] px-5 text-base font-semibold"
          >
            {C.outro}
          </button>
        </div>
      </div>
    );
  }

  // Paso 1: buscar.
  return (
    <div className={CARTAO}>
      <span className={PASSO}>{C.passo1}</span>
      <label className="flex min-h-[52px] items-center gap-2 rounded-2xl border-2 border-[#feae2c] px-3.5">
        <Search className="h-5 w-5 shrink-0 text-[#5e5e66]" aria-hidden="true" />
        <span className="sr-only">{C.passo1}</span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={C.placeholder}
          autoComplete="off"
          className="min-w-0 flex-1 bg-transparent text-base outline-none"
        />
        {buscando && <Loader2 className="h-4 w-4 animate-spin text-[#feae2c]" aria-hidden="true" />}
      </label>
      {mensagemErro}
      {escolhido ? (
        <div className="flex min-h-[52px] items-center gap-2.5 rounded-xl bg-[#fafafa] px-3 py-3" aria-live="polite">
          <MapPin className="h-[18px] w-[18px] shrink-0 text-[#9a5700]" aria-hidden="true" />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-[15px] font-semibold">{escolhido.nome}</span>
            {escolhido.morada && <span className="text-[13px] text-[#5e5e66]">{escolhido.morada}</span>}
          </span>
          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-[#feae2c]" aria-label={C.aPreparar} />
        </div>
      ) : null}
      {!escolhido && resultados && resultados.length === 0 && !buscando && (
        <p className="m-0 text-sm text-[#5e5e66]">{C.semResultados}</p>
      )}
      {!escolhido && resultados && resultados.length > 0 && (
        <ul className="m-0 flex list-none flex-col p-0">
          {resultados.map((r) => (
            <li key={r.placeId} className="border-b border-[#f0f0f3] last:border-b-0">
              <button
                type="button"
                onClick={() => escolher(r.placeId, r)}
                disabled={aCarregar}
                className="flex min-h-[52px] w-full items-center gap-2.5 px-1.5 py-3 text-left hover:bg-[#fafafa]"
              >
                <MapPin className="h-[18px] w-[18px] shrink-0 text-[#5e5e66]" aria-hidden="true" />
                <span className="flex flex-col gap-0.5">
                  <span className="text-[15px] font-semibold">{r.nome}</span>
                  {r.morada && <span className="text-[13px] text-[#5e5e66]">{r.morada}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <span className="text-[13px] text-[#5e5e66]">{PONTO_DE_PARTIDA.analisarNota}</span>
    </div>
  );
}
